<template>
  <div v-if="isVisible" class="product-recs-toggle">
    <div class="product-recs-toggle__header">
      <div class="product-recs-toggle__tabs" role="tablist">
        <template v-if="showToggle">
          <button
            type="button"
            role="tab"
            class="product-recs-toggle__tab"
            :class="{
              'product-recs-toggle__tab--active':
                activeTab === 'recommendations',
            }"
            :aria-selected="activeTab === 'recommendations'"
            @click="selectTab('recommendations')"
          >
            Recommendations
          </button>
          <span class="product-recs-toggle__divider" aria-hidden="true">|</span>
          <button
            type="button"
            role="tab"
            class="product-recs-toggle__tab"
            :class="{
              'product-recs-toggle__tab--active': activeTab === 'recent',
            }"
            :aria-selected="activeTab === 'recent'"
            @click="selectTab('recent')"
          >
            Recently Viewed
          </button>
        </template>
        <h2 v-else class="product-recs-toggle__title">
          {{ singleTabTitle }}
        </h2>
      </div>
    </div>

    <Carousel :key="activeTab">
      <template v-if="activeTab === 'recent'">
        <ProductQueryWrapper
          v-for="item in recentlyViewedProducts"
          :key="item.id"
          v-slot="{ product }"
          :product-id="item.id"
        >
          <ProductCardHelper
            v-if="product"
            v-slot="{ primaryMedia, secondaryMedia }"
            :product="product"
          >
            <ProductCard
              :product="product"
              :media="primaryMedia"
              :hover-media="secondaryMedia"
            />
          </ProductCardHelper>
        </ProductQueryWrapper>
      </template>

      <template v-else>
        <ProductQueryWrapper
          v-for="(recommendation, index) in activeRecommendations"
          :key="recommendation.id || recommendation.product_id"
          v-slot="{ product }"
          :product-id="recommendationProductId(recommendation)"
        >
          <ProductCardHelper
            v-if="product"
            v-slot="{ primaryMedia, secondaryMedia }"
            :product="product"
            :image-type="getImageType(index)"
          >
            <ProductCard
              :media="primaryMedia"
              :hover-media="secondaryMedia"
              :product="productWithReferralPlacement(product)"
            />
          </ProductCardHelper>
        </ProductQueryWrapper>
      </template>
    </Carousel>
  </div>
</template>

<script>
import Carousel from "@/components/Carousel.vue";
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";
import ProductQueryWrapper from "@/components/ProductQueryWrapper.vue";
import productRecommendationsQuery from "@/graphql/queries/ProductRecommendations.gql";
import { reccsImageSequence } from "@/helpers/imageSequences.js";
import { mapState } from "vuex";

export default {
  name: "ProductRecommendationsToggle",
  components: {
    Carousel,
    ProductCard,
    ProductCardHelper,
    ProductQueryWrapper,
  },
  props: {
    recommendations: {
      type: Array,
      default: () => [],
    },
    placement: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      activeTab: "recommendations",
      userSelectedTab: false,
      shopifyRecommendations: [],
    };
  },
  computed: {
    ...mapState("app", {
      rawRecentlyViewedProducts: "recentlyViewedProducts",
    }),
    ...mapState("product", ["product"]),
    recentlyViewedProducts() {
      const uniqueIds = new Set();
      const currentId = this.product?.id?.split("gid://shopify/Product/")[1];

      return (this.rawRecentlyViewedProducts || [])
        .map((item) => {
          const id = item.id.split("gid://shopify/Product/")[1];
          return {
            ...item,
            id,
          };
        })
        .filter((item) => {
          if (!item.id || item.id === currentId || uniqueIds.has(item.id)) {
            return false;
          }
          uniqueIds.add(item.id);
          return true;
        })
        .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
    },
    nostoRecommendations() {
      return this.recommendations?.length ? this.recommendations : [];
    },
    activeRecommendations() {
      return this.nostoRecommendations.length
        ? this.nostoRecommendations
        : this.shopifyRecommendations;
    },
    hasRecentlyViewed() {
      return this.recentlyViewedProducts.length > 0;
    },
    hasRecommendations() {
      return this.activeRecommendations.length > 0;
    },
    showToggle() {
      return this.hasRecentlyViewed && this.hasRecommendations;
    },
    isVisible() {
      return this.hasRecentlyViewed || this.hasRecommendations;
    },
    singleTabTitle() {
      return this.hasRecommendations ? "Recommendations" : "Recently Viewed";
    },
  },
  watch: {
    hasRecentlyViewed: {
      immediate: true,
      handler() {
        this.syncActiveTab();
      },
    },
    hasRecommendations() {
      this.syncActiveTab();
    },
  },
  methods: {
    selectTab(tab) {
      this.userSelectedTab = true;
      this.activeTab = tab;
    },
    syncActiveTab() {
      // Prefer Recommendations by default once it has products.
      // Only fall back to Recently Viewed when Recommendations are empty.
      if (this.userSelectedTab) {
        if (
          this.activeTab === "recommendations" &&
          !this.hasRecommendations &&
          this.hasRecentlyViewed
        ) {
          this.activeTab = "recent";
        } else if (
          this.activeTab === "recent" &&
          !this.hasRecentlyViewed &&
          this.hasRecommendations
        ) {
          this.activeTab = "recommendations";
        }
        return;
      }

      if (this.hasRecommendations) {
        this.activeTab = "recommendations";
      } else if (this.hasRecentlyViewed) {
        this.activeTab = "recent";
      }
    },
    getImageType(index) {
      return reccsImageSequence[index % reccsImageSequence.length];
    },
    recommendationProductId(recommendation) {
      return (
        recommendation.product_id ||
        recommendation.id?.split?.("gid://shopify/Product/")[1] ||
        recommendation.id
      );
    },
    productWithReferralPlacement(product) {
      if (!this.placement || !product?.onlineStoreUrl) {
        return product;
      }

      const newOnlineStoreUrl = new URL(product.onlineStoreUrl);
      newOnlineStoreUrl.searchParams.set("ref-placement", this.placement);

      return {
        ...product,
        onlineStoreUrl: newOnlineStoreUrl,
      };
    },
  },
  apollo: {
    shopifyRecommendations: {
      query: productRecommendationsQuery,
      skip() {
        return this.nostoRecommendations.length > 0 || !this.product?.id;
      },
      variables() {
        return {
          productId: this.product.id,
        };
      },
      update(data) {
        return data?.productRecommendations || [];
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product-recs-toggle {
  background: $color-white;
  padding: 60px 0;

  @include respond-above(md) {
    padding: 80px 0;
  }

  &__header {
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
    padding: 0 20px;
    min-height: 32px;

    @include respond-above(md) {
      margin-bottom: 50px;
      padding: 0 120px;
    }
  }

  &__tabs {
    display: flex;
    align-items: baseline;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 0;
  }

  &__tab {
    appearance: none;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: rgba($color-black, 0.65);
    @include editorial-headline(15px);
    text-transform: uppercase;
    position: relative;
    display: inline-block;
    line-height: 1;
    white-space: nowrap;

    @include respond-above(md) {
      @include editorial-headline(28px);
      line-height: 1;
    }

    &--active {
      color: $color-black;
      text-decoration: underline dotted;
      text-decoration-thickness: from-font;
      text-underline-offset: from-font;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
    }
  }

  &__divider {
    color: $color-black;
    @include editorial-headline(15px);
    user-select: none;
    padding: 0 12px;
    line-height: 1;

    @include respond-above(md) {
      @include editorial-headline(28px);
      padding: 0 16px;
      line-height: 1;
    }
  }

  &__title {
    margin: 0;
    color: $color-black;
    @include editorial-headline(15px);
    text-transform: uppercase;
    text-align: center;
    line-height: 1;

    @include respond-above(md) {
      @include editorial-headline(28px);
      line-height: 1;
    }
  }

  // Carousel inset: 20px mobile / 40px desktop
  :deep(.carousel) {
    padding-left: 20px;

    @include respond-above(md) {
      padding-left: 40px;
    }

    & > * {
      scroll-margin-left: 20px;

      @include respond-above(md) {
        scroll-margin-left: 40px;
      }
    }
  }
}
</style>
